import React from 'react';
import {useStaticQuery, graphql} from 'gatsby';
import Img from 'gatsby-image';

import {Page, Grid, Text, Input} from '../../components';
import RecognitionDemo from './components/RecognitionDemo';
import ReactModal from 'react-modal';

import styles from './Home.module.scss';
import One from '../../images/compressed/123-01-01.png';
import Two from '../../images/compressed/123-02-02.png';
import Three from '../../images/compressed/123-03-03.png';

ReactModal.setAppElement('#___gatsby');

interface Props {
  data?: any;
}

type State = {
  isModalOpen: boolean;
  isContactModalOpen: boolean;
};
type ComposedProps = Props;

const DefaultModal = ({isModalOpen, handleModalClose}) => (
  <ReactModal
    isOpen={isModalOpen}
    onRequestClose={handleModalClose}
    contentLabel="Example Modal In Gatsby"
    className={styles.Modal}
    overlayClassName={styles.ModalOverlay}
  >
    <div className={styles.ModalClose} onClick={handleModalClose} />
    <Text.Container>
      <Text tag="h2" color="var(--color-primary)">
        Join Ekipo now, it's fun!
      </Text>
      <Text>
        Join Ekipo and watch your remote team become closer, happier, and more
        productive! You will be redirected to our dashboard once your email has
        been submitted.
      </Text>
    </Text.Container>
    <form
      name="Sign Up"
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
    >
      <input type="hidden" name="bot-field" />
      <input type="hidden" name="form-name" value="Sign Up" />
      <Input
        name="email"
        label="email"
        type="email"
        id="email"
        placeholder="Ex. example@email.com"
        required
      />
      <Text size="small">
        <input required type="radio" name="role" value="founder" />
        <label htmlFor="founder" className={styles.RadioLabel}>
          Founder
        </label>
        <input required type="radio" name="role" value="hr" />
        <label htmlFor="hr" className={styles.RadioLabel}>
          HR
        </label>
        <input required type="radio" name="role" value="developer" />
        <label htmlFor="developer" className={styles.RadioLabel}>
          Developer
        </label>
        <input required type="radio" name="role" value="other" />
        <label htmlFor="other" className={styles.RadioLabel}>
          Other
        </label>
      </Text>

      <button
        type="submit"
        value="submit"
        style={{marginTop: '10px'}}
        className={styles.PricingCtaButton}
      >
        Start Now
      </button>
      <Text size="subscript" italic>
        *No credit cards required, first 60 days are free for the basic plan
      </Text>
    </form>
  </ReactModal>
);

const ContactSalesModal = ({isModalOpen, handleModalClose}) => (
  <ReactModal
    isOpen={isModalOpen}
    onRequestClose={handleModalClose}
    contentLabel="Example Modal In Gatsby"
    className={styles.Modal}
    overlayClassName={styles.ModalOverlay}
  >
    <div className={styles.ModalClose} onClick={handleModalClose} />
    <Text.Container>
      <Text tag="h2" color="var(--color-primary)">
        Join Ekipo now, it's fun!
      </Text>
      <Text>
        Need help integrating our API with your current workflow? Shoot us a
        message and we will get that built for you.
      </Text>
    </Text.Container>
    <form
      name="Contact Sales"
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
    >
      <input type="hidden" name="bot-field" />
      <input type="hidden" name="form-name" value="Contact Sales" />
      <Input
        name="email"
        label="email"
        type="email"
        id="email"
        placeholder="Ex. example@email.com"
        required
      />
      <Input
        textarea
        name="message"
        label="message"
        type="message"
        id="message"
        placeholder="Write your message here"
      />
      <Text size="small">
        <input required type="radio" name="role" value="founder" />
        <label htmlFor="founder" className={styles.RadioLabel}>
          Founder
        </label>
        <input required type="radio" name="role" value="hr" />
        <label htmlFor="hr" className={styles.RadioLabel}>
          HR
        </label>
        <input required type="radio" name="role" value="developer" />
        <label htmlFor="developer" className={styles.RadioLabel}>
          Developer
        </label>
        <input required type="radio" name="role" value="other" />
        <label htmlFor="other" className={styles.RadioLabel}>
          Other
        </label>
      </Text>

      <button
        type="submit"
        value="submit"
        style={{marginTop: '10px'}}
        className={styles.PricingCtaButton}
      >
        Start Now
      </button>
      <Text size="subscript" italic>
        *No credit cards required, first 60 days are free for the basic plan
      </Text>
    </form>
  </ReactModal>
);

class Home extends React.PureComponent<ComposedProps, State> {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      isContactModalOpen: false,
    };
  }
  handleModalOpen = () => {
    this.setState({isModalOpen: true});

    if (process.env.NODE_ENV === 'production') {
      // window object does not exist in Webpack's Node environment, this is to prevent the error
      const browserWindow: any = typeof window !== 'undefined' ? window : null;
      browserWindow.ga('send', {
        hitType: 'event',
        eventCategory: 'Sign up modal',
        eventAction: 'open',
        eventLabel: 'CTA Experiment',
      });
    }
  };

  handleContactModalOpen = () => {
    this.setState({isContactModalOpen: true});

    if (process.env.NODE_ENV === 'production') {
      // window object does not exist in Webpack's Node environment, this is to prevent the error
      const browserWindow: any = typeof window !== 'undefined' ? window : null;
      browserWindow.ga('send', {
        hitType: 'event',
        eventCategory: 'Contact modal',
        eventAction: 'open',
        eventLabel: 'CTA Experiment',
      });
    }
  };

  handleModalClose = () => {
    this.setState({isModalOpen: false, isContactModalOpen: false});

    if (process.env.NODE_ENV === 'production') {
      // window object does not exist in Webpack's Node environment, this is to prevent the error
      const browserWindow: any = typeof window !== 'undefined' ? window : null;
      browserWindow.ga('send', {
        hitType: 'event',
        eventCategory: 'Sign up modal',
        eventAction: 'close',
        eventLabel: 'CTA Experiment',
      });
    }
  };

  render() {
    const {
      screenshotImage: {
        childImageSharp: {
          fixed: screenshotImageFixed,
          fluid: screenshotImageFluid,
        },
      },
      weeklyRoundupImage: {
        childImageSharp: {fluid: weeklyRoundupImageFluid},
      },
    } = this.props.data;

    return (
      <Page title="Ekipo | Foster Remote Work Culture" disableSiteName>
        <Page.Layout>
          {/* ================================================================================================================= */}
          {/* HERO SECTION */}
          {/* ================================================================================================================= */}
          <div className={styles.HeroSection}>
            <div className={styles.HeroContent}>
              <Grid>
                <Grid.ScreenWidth>
                  <div className={styles.HeroText}>
                    <Text tag="h1" className={styles.HeroTitle}>
                      Foster a happy & productive remote culture with Ekipo.
                    </Text>
                    <Text className={styles.HeroTagline}>
                      We build strong, productive remote teams by nurturing{' '}
                      <b>trust & recognition</b> using <b>fun & rewards</b>.
                    </Text>
                    <div onClick={this.handleModalOpen} className={styles.Cta}>
                      Start for free
                    </div>
                  </div>
                  <div className={styles.HeroImageContainer}>
                    {/* <img className={styles.HeroImage} src={RewardsImage} /> */}
                    <Img
                      className={styles.HeroImage}
                      fixed={screenshotImageFixed}
                    />
                  </div>
                </Grid.ScreenWidth>
              </Grid>
            </div>
          </div>
          <div style={{backgroundColor: '#fffbf2'}}>
            <Page.Section padded>
              <Grid>
                <Text
                  tag="h2"
                  size="h1"
                  align="center"
                  color="var(--color-primary)"
                >
                  How It Works.
                </Text>
                <Text align="center" />
                <div className={styles.Blob}>
                  <div className={styles.BlobItem}>
                    <img src={One} />
                    <Text tag="h3" size="small" align="center">
                      Share Points through our Slack plugin
                    </Text>
                  </div>
                  <div className={styles.BlobItem}>
                    <img src={Two} />
                    <Text tag="h3" size="small" align="center">
                      Redeem Rewards in our Ekipo dashboard
                    </Text>
                  </div>
                  <div className={styles.BlobItem}>
                    <img src={Three} />
                    <Text tag="h3" size="small" align="center">
                      Recognize work with auto-generated newsletters
                    </Text>
                  </div>
                </div>
              </Grid>
            </Page.Section>
          </div>
          {/* ================================================================================================================= */}
          {/* RECOGNITION SECTION */}
          {/* ================================================================================================================= */}
          <Page.Section>
            <Grid>
              <Text
                tag="h2"
                size="h1"
                align="center"
                color="var(--color-primary)"
              >
                Celebrate Good Work.
              </Text>
              <Text align="center">
                Show recognition of good work by handing out points (in the form
                of emojis of your choice) to your coworkers with our{' '}
                <b>Slack Plugin</b>. These points are used as currencies to
                redeem goodies that everyone in the company can enjoy.
              </Text>
              <RecognitionDemo />
            </Grid>
          </Page.Section>
          {/* ================================================================================================================= */}
          {/* REWARDS SECTION */}
          {/* ================================================================================================================= */}
          <div style={{backgroundColor: 'aliceblue'}}>
            <Page.Section padded>
              <Grid>
                <Text
                  tag="h2"
                  size="h1"
                  align="center"
                  color="var(--color-primary)"
                >
                  Team Rewards For All.
                </Text>
                <Text align="center">
                  Contribute your points to the <b>Crowdfunding System</b> to
                  redeem company-wide rewards and make the world a happier
                  place. You can set your rewards to anything you want to match
                  your company culture, have fun with it!
                </Text>
                <div className={styles.FeatureImage}>
                  <Img fluid={screenshotImageFluid} />
                </div>
              </Grid>
            </Page.Section>
          </div>
          {/* ================================================================================================================= */}
          {/* LIVE FEED SECTION */}
          {/* ================================================================================================================= */}
          <Page.Section>
            <Grid>
              <Text
                tag="h2"
                size="h1"
                align="center"
                color="var(--color-primary)"
              >
                Let Everyone Know.
              </Text>
              <Text align="center">
                Let the whole world know about everyone's accomplishments with
                our automated <b>Weekly Newsletter</b>. Ekipo will identify all
                important work through your team's Slack messages and will
                generate a lovely email sent to your team so that everyone knows
                about the great work that's being done.
              </Text>
              <div className={styles.FeatureImage}>
                <Img fluid={weeklyRoundupImageFluid} />
              </div>
            </Grid>
          </Page.Section>
          {/* ================================================================================================================= */}
          {/* PRICING SECTION */}
          {/* ================================================================================================================= */}
          <Page.Section padded>
            <Grid>
              <Text
                tag="h2"
                size="h1"
                align="center"
                color="var(--color-primary)"
              >
                Simple Pricing
              </Text>
              <Text align="center">
                <b>
                  First 8 users free, $6 / user / month starting the 9th user
                </b>
                . 60 days free trial. No credit cards needed.
              </Text>
              <div className={styles.Pricing}>
                <div className={styles.PricingHead}>
                  <Text italic size="small">
                    Only start paying when your team is growing
                  </Text>
                </div>
                <div className={styles.PricingFree}>
                  <div className={styles.PanelHeader}>
                    <Text.Container align="center">
                      <Text tag="h3" color="var(--color-primary)">
                        8 Free Users
                      </Text>
                      <Text color="var(--color-primary)" italic size="small">
                        Free Plan
                      </Text>
                      <Text
                        size="subscript"
                        italic
                        color="var(--color-primary)"
                      >
                        *No credit cards required
                      </Text>
                    </Text.Container>
                  </div>
                </div>
                <div className={styles.PricingBasic}>
                  <div className={styles.PanelHeader}>
                    <Text.Container align="center">
                      <Text tag="h3" color="var(--color-primary)">
                        $6 / month
                      </Text>
                      <Text color="var(--color-primary)" italic size="small">
                        Team Plan
                      </Text>
                      <Text
                        size="subscript"
                        italic
                        color="var(--color-primary)"
                      >
                        *60 days free-trial, no credit cards required
                      </Text>
                    </Text.Container>
                  </div>
                </div>
                <div className={styles.PanelBasic}>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Employee profiles</Text>
                  </div>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Point sharing integration (Slack)</Text>
                  </div>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Crowdfunding rewards</Text>
                  </div>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Weekly roundup emails</Text>
                  </div>
                </div>
                <div className={styles.PricingEnterprise}>
                  <div className={styles.PanelHeader}>
                    <Text.Container align="center">
                      <Text tag="h3" color="var(--color-primary)">
                        Contact sales
                      </Text>
                      <Text color="var(--color-primary)" italic size="small">
                        Enterprise Plan
                      </Text>
                    </Text.Container>
                  </div>
                </div>
                <div className={styles.PanelEnterprise}>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Employee profiles</Text>
                  </div>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Point sharing integration</Text>
                  </div>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Crowdfunding reward system</Text>
                  </div>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Weekly roundup emails</Text>
                  </div>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Custom dashboard</Text>
                  </div>
                  <div className={styles.PanelContentItem}>
                    <Text size="small">Full API access</Text>
                  </div>
                </div>
                <div className={styles.PricingCta}>
                  <button
                    onClick={this.handleModalOpen}
                    className={styles.PricingCtaButton}
                  >
                    Start Now
                  </button>
                  <Text size="subscript" italic>
                    *No credit cards required, first 60 days are free for the
                    basic plan
                  </Text>
                </div>
                <div className={styles.PricingContact}>
                  <button
                    onClick={this.handleContactModalOpen}
                    className={styles.PricingCtaButton}
                  >
                    Contact Sales
                  </button>
                  <Text size="subscript" italic>
                    *Prices will vary based on requirements
                  </Text>
                </div>
              </div>
            </Grid>
          </Page.Section>
          <div className={styles.Ending}>
            <Grid>
              <Text.Container align="center">
                <Text tag="h5" size="h2" color="white">
                  Let's build a happier remote work culture that is more
                  connected, more productive, and more human.
                </Text>
                <div onClick={this.handleModalOpen} className={styles.Cta}>
                  Try it now
                </div>
              </Text.Container>
            </Grid>
          </div>
          <ContactSalesModal
            isModalOpen={this.state.isContactModalOpen}
            handleModalClose={this.handleModalClose}
          />
          <DefaultModal
            isModalOpen={this.state.isModalOpen}
            handleModalClose={this.handleModalClose}
          />
        </Page.Layout>
      </Page>
    );
  }
}

export default () => {
  const data = useStaticQuery(graphql`
    query {
      screenshotImage: file(
        relativePath: {eq: "compressed/rewards-screenshot.png"}
      ) {
        childImageSharp {
          # Specify a fluid image and fragment
          # The default maxWidth is 800 pixels
          fixed(width: 640) {
            ...GatsbyImageSharpFixed
          }
          fluid(maxWidth: 2560, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }

      weeklyRoundupImage: file(
        relativePath: {eq: "compressed/screenshot.png"}
      ) {
        childImageSharp {
          fluid(maxWidth: 2560, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  return <Home data={data} />;
};
